import React from 'react';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';

export interface TablePaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  className?: string;
  showPageSizeSelector?: boolean;
  showInfo?: boolean;
}

const TablePagination: React.FC<TablePaginationProps> = ({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
  className = '',
  showPageSizeSelector = true,
  showInfo = true
}) => {
  // pageSize === 0 means "All" — one page showing every row.
  const showingAll = pageSize <= 0;
  const startItem = showingAll ? (totalCount > 0 ? 1 : 0) : (currentPage - 1) * pageSize + 1;
  const endItem = showingAll ? totalCount : Math.min(currentPage * pageSize, totalCount);

  const generatePageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push('...');
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('...');
      }

      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Always show pagination if we have data, regardless of page count
  if (totalCount === 0) {
    return null;
  }

  return (
    <div className={`bg-white px-6 py-4 flex items-center justify-between border-t border-gray-200 sm:px-8 rounded-b-lg ${className}`}>
      <div className="flex-1 flex justify-between items-center">
        {showInfo && (
          <div className="flex items-center gap-6">
            <p className="text-base text-gray-700">
              عرض{' '}
              <span className="font-medium">{startItem}</span>
              {' '}إلى{' '}
              <span className="font-medium">{endItem}</span>
              {' '}من{' '}
              <span className="font-medium">{totalCount}</span>
              {' '}نتيجة
            </p>

            {showPageSizeSelector && (
              <div className="flex items-center gap-3">
                <label className="text-base text-gray-700">عرض:</label>
                <select
                  value={pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  className="border border-gray-300 rounded-md px-2 py-1 text-base focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {PAGINATION_CONFIG.PAGE_SIZE_OPTIONS.map(size => (
                    <option key={size} value={size}>
                      {size === 0 ? 'الكل' : size}
                    </option>
                  ))}
                </select>
                <span className="text-base text-gray-700">في الصفحة</span>
              </div>
            )}
          </div>
        )}

        {/* Navigation Buttons - Always show if we have data */}
        <div className="flex items-center space-x-3 space-x-reverse">
          {/* Previous Button */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`relative inline-flex items-center px-4 py-2 text-base font-medium rounded-md ${
              currentPage === 1
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-500 hover:text-primary hover:bg-brand-50'
            }`}
          >
            السابق
          </button>

          {/* Page Numbers - Only show if we have multiple pages */}
          {totalPages > 1 && (
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              {generatePageNumbers().map((pageNum, index) => {
                if (pageNum === '...') {
                  return (
                    <span
                      key={`ellipsis-${index}`}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-base font-medium text-gray-700"
                    >
                      ...
                    </span>
                  );
                }

                const pageNumber = pageNum as number;
                const isActive = pageNumber === currentPage;

                return (
                  <button
                    key={pageNumber}
                    onClick={() => onPageChange(pageNumber)}
                    className={`relative inline-flex items-center px-4 py-2 border text-base font-medium ${
                      isActive
                        ? 'z-10 bg-brand-50 border-brand-500 text-brand-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-brand-50 hover:text-primary hover:border-brand-300'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </nav>
          )}

          {/* Next Button */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`relative inline-flex items-center px-4 py-2 text-base font-medium rounded-md ${
              currentPage === totalPages
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-500 hover:text-primary hover:bg-brand-50'
            }`}
          >
            التالي
          </button>
        </div>
      </div>
    </div>
  );
};

export default TablePagination;
